// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierDeck.jsx   ·   THE DECK, THE RAILS, THE PILINGS
// ────────────────────────────────────────────────────────────────────
// Derived ENTIRELY from the map and the boardwalk plan. Nothing here is
// placed by hand: a new wing in buildMap() grows planks, fascia, rails,
// piles, braces and string lights the moment it exists, and a new row
// in SHOPFRONTS opens its stretch of railing and stands on its own apron.
//
// ⭐ A REAL PIER, BOTTOM TO TOP:
//   piles into the sea, X-braced along every outer run
//   → a deck slab with a fascia board you can see from off the pier
//   → boards running ACROSS the walk (world-space UVs, so they run on
//     unbroken from tile to tile)
//   → posts, a cap rail, a mid rail, balusters and a kick board
//   → a string of lights over the rail.
// Railings stop where a shopfront stands — you do not walk into a shop
// through a fence.
//
// ⚑ A HANDFUL OF DRAWS FOR ALL OF IT: planks one mesh, timber one mesh,
// rails one mesh, piles and waterline rings instanced, every bulb one
// Points field. Zero lights.
// ════════════════════════════════════════════════════════════════════

import { useMemo, useLayoutEffect, useEffect, useRef } from "react";
import * as THREE from "three";
import { Merger, boxUV, mulberry32 } from "./pierTex";
import { deckTex, timberTex, dotTex } from "./pierPaint";
import { Merged } from "./pierBuild";

// ⭐ THE SEA LEVEL. The ocean of Chao sits this far under the planks.
// Pier3D hands the same number to the horizon so the piles and the water
// always meet. A seaside pier stands a few metres up, not a cliff's
// height — a person here is ~1.5 units tall.
export const SEA_Y = -3.2;
const RAIL_H = 1.02;
const VOID = 2;

// Every frontage, normalised by the plan (pierPlan.js FRONTS):
// { face, edge, a0, a1 } — `a` runs along the edge (x for a N/S front,
// y for an E/W one). The Hall of Deeds is one of them.
function frontsOf(world) {
  return world.fronts || [];
}

export function Deck({ world }) {
  const { map, cols, rows } = world;

  const built = useMemo(() => {
    const solid = (x, y) => y >= 0 && y < rows && x >= 0 && x < cols && map[y][x] !== VOID;
    const fronts = frontsOf(world);
    const frontedAt = (along, line, face) => fronts.some((f) =>
      f.face === face && Math.abs(f.edge - line) < 0.01 && along >= f.a0 - 0.001 && along <= f.a1 + 0.001);
    const alleys = world.alleys || [];
    const alleyAt = (along, line, face) => alleys.some((g) =>
      g.face === face && Math.abs(g.edge - line) < 0.01 && along > g.s0 - 0.5 && along < g.s1 + 0.5);

    const top = new Merger();
    const wood = new Merger();
    const piles = new Map();
    const lines = new Map();          // outer pile runs, for the braces
    const bulbs = [];
    const posts = new Set();

    const addPile = (x, z, lineKey, along) => {
      const k = `${Math.round(x * 4)},${Math.round(z * 4)}`;
      if (!piles.has(k)) piles.set(k, [x, z]);
      if (lineKey) {
        if (!lines.has(lineKey)) lines.set(lineKey, new Set());
        lines.get(lineKey).add(Math.round(along * 4) / 4);
      }
    };

    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
      if (!solid(x, y)) continue;
      top.plane("deck", 1, 1, [x, 0, y], [-Math.PI / 2, 0, 0]);
      // ⚠ THE SLAB'S TOP SITS WELL UNDER THE PLANKS. At 1 cm apart the two
      // surfaces z-fought at walking distance and printed a checkerboard of
      // tile squares across every stretch of deck no lamp was washing out.
      wood.box("slab", 1, 0.26, 1, [x, -0.21, y]);
      if (x % 4 === 1 && y % 4 === 2) addPile(x, y);

      for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]]) {
        if (solid(x + dx, y + dy)) continue;
        const horiz = dy !== 0;
        const ex = x + dx * 0.5, ez = y + dy * 0.5;

        const isFront = (dy === -1 && frontedAt(x, y - 0.5, "S")) || (dy === 1 && frontedAt(x, y + 0.5, "N"))
          || (dx === -1 && frontedAt(y, x - 0.5, "E")) || (dx === 1 && frontedAt(y, x + 0.5, "W"));

        // ⚠ the fascia board: its top a hair UNDER the planks, never proud
        // of them — at +6 cm it drew a dark rule across the walk at every
        // shop's apron — and not at all where an apron carries the deck on
        if (!isFront) wood.box("fascia", horiz ? 1.02 : 0.07, 0.42, horiz ? 0.07 : 1.02, [ex + dx * 0.035, -0.22, ez + dy * 0.035]);

        // piles on the edge line at even corners
        const cA = horiz ? [x - 0.5, ez] : [ex, y - 0.5];
        const cB = horiz ? [x + 0.5, ez] : [ex, y + 0.5];
        for (const c of [cA, cB]) {
          const along = horiz ? c[0] : c[1];
          if (Math.round(along + 0.5) % 2 === 0) {
            addPile(c[0], c[1], horiz ? `h${ez}` : `v${ex}`, along);
          }
        }

        if (isFront) continue;
        // an alley between two buildings is fenced below, not railed
        if ((dy === -1 && alleyAt(x, y - 0.5, "S")) || (dy === 1 && alleyAt(x, y + 0.5, "N"))
          || (dx === -1 && alleyAt(y, x - 0.5, "E")) || (dx === 1 && alleyAt(y, x + 0.5, "W"))) continue;

        // ── the railing ──
        const rx = ex - dx * 0.07, rz = ez - dy * 0.07;
        const L = 1.0;
        wood.box("rail", horiz ? L : 0.1, 0.08, horiz ? 0.1 : L, [rx, RAIL_H, rz]);          // cap
        wood.box("rail", horiz ? L : 0.05, 0.06, horiz ? 0.05 : L, [rx, 0.56, rz]);          // mid
        wood.box("rail", horiz ? L : 0.04, 0.13, horiz ? 0.04 : L, [rx, 0.085, rz]);         // kick
        for (const o of [-0.25, 0.25]) {
          const bx = horiz ? rx + o : rx, bz = horiz ? rz : rz + o;
          wood.box("rail", 0.035, 0.4, 0.035, [bx, 0.355, bz]);
          wood.box("rail", 0.035, 0.42, 0.035, [bx, 0.8, bz]);
        }
        for (const c of [cA, cB]) {
          const px = horiz ? c[0] : rx, pz = horiz ? rz : c[1];
          const k = `${Math.round(px * 20)},${Math.round(pz * 20)}`;
          if (posts.has(k)) continue;
          posts.add(k);
          wood.box("rail", 0.11, RAIL_H + 0.12, 0.11, [px, (RAIL_H + 0.12) / 2, pz]);
          wood.box("rail", 0.15, 0.05, 0.15, [px, RAIL_H + 0.14, pz]);                        // post cap
        }
        // the string of lights, sagging between posts
        for (const t of [0.25, 0.75]) {
          const along = t - 0.5;
          bulbs.push([horiz ? rx + along : rx, RAIL_H + 0.36 - 0.07, horiz ? rz : rz + along, (x + y + (t > 0.5 ? 1 : 0)) % 3]);
        }
      }
    }

    // ── every shopfront stands on its own apron over the water ──
    // ⚠ THE APRON'S TOP IS THE DECK, NOT A BOX LID (a lid 6 cm proud left a
    // black pad wherever the fader ghosted a shop). Planks over a low slab.
    for (const f of fronts) {
      // the south row's low buildings are shallower (pierBuild), and so are their aprons
      // every wing is two storeys (3.0 deep) on the pitchfork, whichever way it faces (L91)
      const D = 3.15, a0 = f.a0 - 0.08, a1 = f.a1 + 0.08;
      const ns = f.face === "S" || f.face === "N";
      const back = (f.face === "S" || f.face === "E") ? f.edge - D : f.edge + D;
      const b0 = Math.min(f.edge, back), b1 = Math.max(f.edge, back);
      const [x0, x1, z0, z1] = ns ? [a0, a1, b0, b1] : [b0, b1, a0, a1];
      wood.box("fascia", x1 - x0, 0.36, z1 - z0, [(x0 + x1) / 2, -0.24, (z0 + z1) / 2]);
      top.plane("deck", x1 - x0, z1 - z0, [(x0 + x1) / 2, -0.004, (z0 + z1) / 2], [-Math.PI / 2, 0, 0]);
      const lineKey = ns ? `h${back}` : `v${back}`;
      const n = Math.max(1, Math.ceil((a1 - a0) / 2));
      for (let i = 0; i <= n; i++) {
        const t = a0 + ((a1 - a0) * i) / n;
        if (ns) addPile(t, back, lineKey, t); else addPile(back, t, lineKey, t);
      }
      if (ns) { addPile(a0, (b0 + b1) / 2); addPile(a1, (b0 + b1) / 2); }
      else { addPile((b0 + b1) / 2, a0); addPile((b0 + b1) / 2, a1); }
    }

    // ── ⭐ every alley between two buildings is closed by a board fence ──
    // ⚠ Left as railing, each gap showed the sea, the braces and two lamps
    // through a hole in the street, and read as damage (visual review,
    // 2026-09-14). Planks of slightly uneven height, two rails, two posts.
    for (const g of alleys) {
      const ns = g.face === "S" || g.face === "N";
      const out = (g.face === "S" || g.face === "E") ? -1 : 1;      // toward the water
      const line = g.edge + out * 0.1;
      const s0 = g.s0 - 0.02, s1 = g.s1 + 0.02, L = s1 - s0, mid = (s0 + s1) / 2;
      const at = (along, h, off = 0) => (ns ? [along, h, line + out * off] : [line + out * off, h, along]);
      const plank = (lenAlong, h, thick, p) => wood.box("fence", ns ? lenAlong : thick, h, ns ? thick : lenAlong, p);
      const n = Math.max(3, Math.round(L / 0.22));
      for (let i = 0; i < n; i++) {
        const h = 1.72 + ((i * 7) % 3) * 0.05;
        plank(L / n - 0.025, h, 0.05, at(s0 + (L * (i + 0.5)) / n, h / 2));
      }
      for (const hy of [0.42, 1.38]) plank(L, 0.1, 0.05, at(mid, hy, -0.05));
      for (const a of [s0 + 0.06, s1 - 0.06]) plank(0.11, 1.95, 0.11, at(a, 0.975, -0.02));
    }

    // ── X-braces between neighbouring piles on every outer run ──
    const lo = -0.38, hi = SEA_Y + 0.7;
    for (const [key, set] of lines) {
      const horiz = key[0] === "h";
      const at = parseFloat(key.slice(1));
      const vals = [...set].sort((a, b) => a - b);
      for (let i = 0; i + 1 < vals.length; i++) {
        const a = vals[i], b = vals[i + 1];
        if (b - a > 2.3 || b - a < 0.6) continue;
        const mid = (a + b) / 2, span = b - a, rise = lo - hi;
        const len = Math.hypot(span, rise);
        const ang = Math.atan2(rise, span);
        for (const s of [1, -1]) {
          if (horiz) wood.box("brace", len, 0.1, 0.06, [mid, (lo + hi) / 2, at], [0, 0, s * ang]);
          else wood.box("brace", 0.06, 0.1, len, [at, (lo + hi) / 2, mid], [-s * ang, 0, 0]);
        }
        // a wale along the run, just under the deck
        if (horiz) wood.box("brace", span, 0.16, 0.08, [mid, -0.52, at]);
        else wood.box("brace", 0.08, 0.16, span, [at, -0.52, mid]);
      }
    }

    const geoTop = top.build();
    const geoWood = wood.build();
    if (geoTop.deck) boxUV(geoTop.deck, 0.25);
    ["slab", "fascia", "brace", "rail", "fence"].forEach((k) => geoWood[k] && boxUV(geoWood[k], 1.2));

    // the bulbs, one Points field
    const pos = new Float32Array(bulbs.length * 3);
    const col = new Float32Array(bulbs.length * 3);
    const tints = [new THREE.Color("#ffd98a"), new THREE.Color("#fff1c9"), new THREE.Color("#7ce7ff")];
    bulbs.forEach(([bx, by, bz, ci], i) => {
      pos.set([bx, by, bz], i * 3);
      const c = tints[ci];
      col.set([c.r, c.g, c.b], i * 3);
    });
    const lights = new THREE.BufferGeometry();
    lights.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    lights.setAttribute("color", new THREE.BufferAttribute(col, 3));

    return { geoTop, geoWood, piles: [...piles.values()], lights };
  }, [map, cols, rows, world]);

  const mats = useMemo(() => {
    const d = deckTex();
    d.wrapS = d.wrapT = THREE.RepeatWrapping;
    return {
      deck: new THREE.MeshStandardMaterial({ map: d, roughness: 0.88, metalness: 0 }),
      slab: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#3c2c1e", roughness: 1 }),
      fascia: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#6e5438", roughness: 1 }),
      brace: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#4a3a2a", roughness: 1 }),
      // ⚠ a little self-light of its own colour: vertical timber takes
      // half the key light the deck does and silhouettes into the water
      fence: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#8a6c4c", roughness: 0.95, emissive: "#2a1c10", emissiveIntensity: 0.3 }),
      rail: new THREE.MeshStandardMaterial({
        map: timberTex(), color: "#a8865e", roughness: 0.85,
        emissive: "#4a3420", emissiveIntensity: 0.35,
      }),
      pile: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#54402c", roughness: 1 }),
      ring: new THREE.MeshBasicMaterial({
        color: "#7CE7FF", transparent: true, opacity: 0.32, depthWrite: false,
        blending: THREE.AdditiveBlending, toneMapped: false, fog: false, side: THREE.DoubleSide,
      }),
      bulbs: new THREE.PointsMaterial({
        size: 0.42, map: dotTex(), vertexColors: true, transparent: true,
        depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true,
        toneMapped: false, fog: false,
      }),
    };
  }, []);

  const pileGeo = useMemo(() => new THREE.CylinderGeometry(0.14, 0.17, 1, 9), []);
  const ringGeo = useMemo(() => new THREE.RingGeometry(0.2, 0.36, 18), []);
  const pileRef = useRef();
  const ringRef = useRef();

  useLayoutEffect(() => {
    const m = new THREE.Object3D();
    const R = mulberry32(0x9174);
    const top = -0.3, bottom = SEA_Y - 1.6;
    const len = top - bottom;
    built.piles.forEach(([x, z], i) => {
      m.position.set(x, (top + bottom) / 2, z);
      m.rotation.set((R() - 0.5) * 0.03, R() * 6, (R() - 0.5) * 0.03);   // none of them plumb
      m.scale.set(1, len, 1);
      m.updateMatrix();
      pileRef.current.setMatrixAt(i, m.matrix);
      m.position.set(x, SEA_Y + 0.03, z);
      m.rotation.set(-Math.PI / 2, 0, 0);
      m.scale.setScalar(0.9 + R() * 0.4);
      m.updateMatrix();
      ringRef.current.setMatrixAt(i, m.matrix);
    });
    pileRef.current.instanceMatrix.needsUpdate = true;
    ringRef.current.instanceMatrix.needsUpdate = true;
  }, [built]);

  useEffect(() => () => {
    Object.values(built.geoTop).forEach((g) => g.dispose());
    Object.values(built.geoWood).forEach((g) => g.dispose());
    built.lights.dispose();
  }, [built]);

  return (
    <group>
      <Merged geos={built.geoTop} mats={mats} />
      <Merged geos={built.geoWood} mats={mats} />
      <instancedMesh ref={pileRef} args={[pileGeo, mats.pile, built.piles.length]} frustumCulled={false} />
      <instancedMesh ref={ringRef} args={[ringGeo, mats.ring, built.piles.length]} frustumCulled={false} />
      <points geometry={built.lights} material={mats.bulbs} frustumCulled={false} />
    </group>
  );
}
